import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiDollarSign, FiPercent, FiGift, FiUsers, FiX, FiCheck } from 'react-icons/fi';
import axios from 'axios';

const Memberships = () => {
    const [memberships, setMemberships] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingMembership, setEditingMembership] = useState(null);

    const [newMembership, setNewMembership] = useState({
        name: '',
        displayName: '',
        description: '',
        price: '',
        billingCycle: 'monthly',
        discountPercentage: '',
        maxDiscountPerBooking: '',
        creditsPerBooking: '',
        monthlySubsidy: '',
        subsidyDescription: '',
        perks: [],
        badgeColor: '#888888',
        priority: 0,
        isPopular: false,
        customerValue: '',
        vendorValue: ''
    });

    const [newPerk, setNewPerk] = useState({ title: '', description: '' });

    const API_BASE_URL = `${process.env.REACT_APP_API_URL}/api` || 'https://api.bookwie.com/api';
    const token = localStorage.getItem('token');

    useEffect(() => {
        fetchMemberships();
    }, []);

    const fetchMemberships = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/membership/admin/memberships`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMemberships(response.data.data.memberships);
        } catch (error) {
            console.error('Error fetching memberships:', error);
            alert('Failed to load memberships');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateMembership = async (e) => {
        e.preventDefault();
        
        try {
            const membershipData = {
                ...newMembership,
                price: parseFloat(newMembership.price),
                discountPercentage: parseFloat(newMembership.discountPercentage) || 0,
                maxDiscountPerBooking: newMembership.maxDiscountPerBooking ? parseFloat(newMembership.maxDiscountPerBooking) : null,
                creditsPerBooking: parseFloat(newMembership.creditsPerBooking) || 0,
                monthlySubsidy: parseFloat(newMembership.monthlySubsidy) || 0,
                priority: parseInt(newMembership.priority) || 0
            };

            if (editingMembership) {
                // Update existing membership
                await axios.put(`${API_BASE_URL}/membership/admin/memberships/${editingMembership._id}`, membershipData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                alert('Membership updated successfully! Note: Existing subscribers will keep their original benefits.');
            } else {
                // Create new membership
                await axios.post(`${API_BASE_URL}/membership/admin/memberships`, membershipData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                alert('Membership created successfully!');
            }

            setShowCreateModal(false);
            setEditingMembership(null);
            resetForm();
            fetchMemberships();
        } catch (error) {
            console.error('Error saving membership:', error);
            alert(error.response?.data?.message || 'Failed to save membership');
        }
    };

    const handleEditMembership = (membership) => {
        setEditingMembership(membership);
        setNewMembership({
            name: membership.name,
            displayName: membership.displayName,
            description: membership.description,
            price: membership.price.toString(),
            billingCycle: membership.billingCycle,
            discountPercentage: membership.discountPercentage.toString(),
            maxDiscountPerBooking: membership.maxDiscountPerBooking?.toString() || '',
            creditsPerBooking: membership.creditsPerBooking.toString(),
            monthlySubsidy: membership.monthlySubsidy.toString(),
            subsidyDescription: membership.subsidyDescription || '',
            perks: membership.perks || [],
            badgeColor: membership.badgeColor || '#888888',
            priority: membership.priority,
            isPopular: membership.isPopular,
            customerValue: membership.customerValue || '',
            vendorValue: membership.vendorValue || ''
        });
        setShowCreateModal(true);
    };

    const handleDeleteMembership = async (membershipId) => {
        if (!window.confirm('Are you sure you want to delete this membership?')) {
            return;
        }

        try {
            await axios.delete(`${API_BASE_URL}/membership/admin/memberships/${membershipId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            alert('Membership deleted successfully!');
            fetchMemberships();
        } catch (error) {
            console.error('Error deleting membership:', error);
            alert(error.response?.data?.message || 'Failed to delete membership');
        }
    };

    const handleToggleStatus = async (membershipId) => {
        try {
            await axios.patch(
                `${API_BASE_URL}/membership/admin/memberships/${membershipId}/toggle`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            alert('Membership status updated!');
            fetchMemberships();
        } catch (error) {
            console.error('Error toggling status:', error);
            alert('Failed to update membership status');
        }
    };

    const addPerk = () => {
        if (newPerk.title) {
            setNewMembership({
                ...newMembership,
                perks: [...newMembership.perks, { ...newPerk }]
            });
            setNewPerk({ title: '', description: '' });
        }
    };

    const removePerk = (index) => {
        setNewMembership({
            ...newMembership,
            perks: newMembership.perks.filter((_, i) => i !== index)
        });
    };

    const resetForm = () => {
        setNewMembership({
            name: '',
            displayName: '',
            description: '',
            price: '',
            billingCycle: 'monthly',
            discountPercentage: '',
            maxDiscountPerBooking: '',
            creditsPerBooking: '',
            monthlySubsidy: '',
            subsidyDescription: '',
            perks: [],
            badgeColor: '#888888',
            priority: 0,
            isPopular: false,
            customerValue: '',
            vendorValue: ''
        });
        setNewPerk({ title: '', description: '' });
        setEditingMembership(null);
    };

    const loadSilverTemplate = () => {
        setNewMembership({
            name: 'silver',
            displayName: 'Silver Pass',
            description: '10% off on all services with exclusive perks',
            price: '199',
            billingCycle: 'monthly',
            discountPercentage: '10',
            maxDiscountPerBooking: '500',
            creditsPerBooking: '25',
            monthlySubsidy: '100',
            subsidyDescription: 'Bookwie-funded small touch-up',
            perks: [
                { title: 'Verified Beauticians & Salons', description: 'Access to verified professionals' },
                { title: 'StyleMatch AI suggestions', description: 'AI-powered style recommendations' },
                { title: 'Quality Guarantee', description: 'Satisfaction guaranteed' },
                { title: 'Revisit Reward', description: '₹25 extra credit on repeat within 30-45 days' }
            ],
            badgeColor: '#C0C0C0',
            priority: 1,
            isPopular: false,
            customerValue: 'Up to ₹400 worth benefit monthly (2× return)',
            vendorValue: 'Capped discount, increased repeat customers'
        });
    };

    const loadGoldTemplate = () => {
        setNewMembership({
            name: 'gold',
            displayName: 'Gold Pass',
            description: '20% off with premium benefits',
            price: '399',
            billingCycle: 'monthly',
            discountPercentage: '20',
            maxDiscountPerBooking: '700',
            creditsPerBooking: '50',
            monthlySubsidy: '200',
            subsidyDescription: 'Bookwie-funded express service',
            perks: [
                { title: 'Gold Badge Recognition', description: 'Premium member status' },
                { title: 'Bookwie Circle Access', description: 'Exclusive community' },
                { title: 'Creator-Style Expert sessions', description: 'Learn from experts' },
                { title: 'Before-After Gallery', description: 'Premium visibility' },
                { title: 'Priority Support', description: 'Verified status boost' }
            ],
            badgeColor: '#FFD700',
            priority: 2,
            isPopular: true,
            customerValue: '₹1,000+ monthly perceived benefit (≈3× return)',
            vendorValue: 'Higher loyalty from premium segment users'
        });
    };

    return (
        <div className="card p-5 md:p-8">
            <div className="mb-10">
                <p className="text-3xl font-extrabold tracking-tight text-slate-900">
                    Membership Plans
                </p>
                <p className="text-gray-500 mt-2">Create and manage subscription memberships</p>
            </div>

            {/* Action Button */}
            <div className="flex flex-wrap gap-4 mb-6">
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
                >
                    <FiPlus /> Create Membership
                </button>
            </div>

            {/* Memberships List */}
            {loading ? (
                <div className="flex justify-center items-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
            ) : memberships.length === 0 ? (
                <div className="text-center py-20">
                    <FiGift className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No memberships found. Create your first one!</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {memberships.map((membership) => (
                        <div
                            key={membership._id}
                            className="border-2 border-gray-200 rounded-xl p-6 hover:shadow-xl transition-shadow relative"
                            style={{ borderColor: membership.badgeColor + '40' }}
                        >
                            {/* Header */}
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div 
                                            className="w-12 h-12 rounded-full flex items-center justify-center"
                                            style={{ backgroundColor: membership.badgeColor + '20' }}
                                        >
                                            <FiGift className="w-6 h-6" style={{ color: membership.badgeColor }} />
                                        </div>
                                        {membership.isPopular && (
                                            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-semibold">
                                                Popular
                                            </span>
                                        )}
                                        {membership.isActive ? (
                                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Active</span>
                                        ) : (
                                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">Inactive</span>
                                        )}
                                    </div>
                                    <h3 className="text-2xl font-bold mb-1">{membership.displayName}</h3>
                                    <p className="text-gray-600 text-sm mb-3">{membership.description}</p>
                                </div>
                            </div>

                            {/* Price */}
                            <div className="mb-4">
                                <div className="flex items-baseline gap-2">
                                    <span className="text-4xl font-bold text-blue-600">₹{membership.price}</span>
                                    <span className="text-gray-500">/{membership.billingCycle}</span>
                                </div>
                            </div>

                            {/* Benefits */}
                            <div className="space-y-3 mb-4">
                                <div className="flex items-center gap-2 text-sm">
                                    <FiPercent className="text-green-600" />
                                    <span><strong>{membership.discountPercentage}% OFF</strong> (up to ₹{membership.maxDiscountPerBooking})</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                    <FiGift className="text-purple-600" />
                                    <span><strong>₹{membership.creditsPerBooking}</strong> credits per booking</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                    <FiDollarSign className="text-blue-600" />
                                    <span><strong>₹{membership.monthlySubsidy}</strong> monthly subsidy</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                    <FiUsers className="text-orange-600" />
                                    <span><strong>{membership.totalSubscribers}</strong> subscribers</span>
                                </div>
                            </div>

                            {/* Perks */}
                            {membership.perks && membership.perks.length > 0 && (
                                <div className="mb-4">
                                    <p className="text-sm font-semibold mb-2">Perks:</p>
                                    <ul className="text-xs text-gray-600 space-y-1">
                                        {membership.perks.slice(0, 3).map((perk, i) => (
                                            <li key={i} className="flex items-start gap-1">
                                                <FiCheck className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                                                <span>{perk.title}</span>
                                            </li>
                                        ))}
                                        {membership.perks.length > 3 && (
                                            <li className="text-blue-600">+{membership.perks.length - 3} more</li>
                                        )}
                                    </ul>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex gap-2 mt-6 pt-4 border-t">
                                <button
                                    onClick={() => handleEditMembership(membership)}
                                    className="px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                    title="Edit membership (only affects new subscribers)"
                                >
                                    <FiEdit2 />
                                </button>
                                <button
                                    onClick={() => handleToggleStatus(membership._id)}
                                    className={`flex-1 px-3 py-2 rounded text-sm font-medium ${
                                        membership.isActive
                                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                                    }`}
                                >
                                    {membership.isActive ? 'Deactivate' : 'Activate'}
                                </button>
                                <button
                                    onClick={() => handleDeleteMembership(membership._id)}
                                    disabled={membership.totalSubscribers > 0}
                                    className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title={membership.totalSubscribers > 0 ? 'Cannot delete membership with active subscribers' : 'Delete membership'}
                                >
                                    <FiTrash2 />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Membership Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto my-8">
                        <div className="sticky top-0 bg-white border-b p-6 flex items-center justify-between z-10">
                            <div>
                                <h3 className="text-xl font-bold">
                                    {editingMembership ? 'Edit Membership' : 'Create New Membership'}
                                </h3>
                                {editingMembership && (
                                    <p className="text-sm text-orange-600 mt-1">
                                        ⚠️ Changes only affect new subscribers. Existing subscribers keep original benefits.
                                    </p>
                                )}
                            </div>
                            <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="p-2 hover:bg-gray-100 rounded">
                                <FiX className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleCreateMembership} className="p-6">
                            {/* Quick Templates - Only show when creating new */}
                            {!editingMembership && (
                                <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                                    <p className="text-sm font-semibold mb-2">Quick Templates:</p>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={loadSilverTemplate}
                                            className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 text-sm font-medium"
                                        >
                                            Load Silver Pass Template
                                        </button>
                                        <button
                                            type="button"
                                            onClick={loadGoldTemplate}
                                            className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 text-sm font-medium"
                                        >
                                            Load Gold Pass Template
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                {/* Basic Info */}
                                <div>
                                    <label className="block text-sm font-medium mb-2">Internal Name *</label>
                                    <input
                                        type="text"
                                        required
                                        value={newMembership.name}
                                        onChange={(e) => setNewMembership({...newMembership, name: e.target.value})}
                                        placeholder="silver, gold, platinum"
                                        className="w-full px-4 py-2 border rounded-lg"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Display Name *</label>
                                    <input
                                        type="text"
                                        required
                                        value={newMembership.displayName}
                                        onChange={(e) => setNewMembership({...newMembership, displayName: e.target.value})}
                                        placeholder="Silver Pass"
                                        className="w-full px-4 py-2 border rounded-lg"
                                    />
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-sm font-medium mb-2">Description</label>
                                    <textarea
                                        value={newMembership.description}
                                        onChange={(e) => setNewMembership({...newMembership, description: e.target.value})}
                                        placeholder="Brief description"
                                        rows="2"
                                        className="w-full px-4 py-2 border rounded-lg"
                                    />
                                </div>

                                {/* Pricing */}
                                <div>
                                    <label className="block text-sm font-medium mb-2">Price (₹) *</label>
                                    <input
                                        type="number"
                                        required
                                        min="0"
                                        value={newMembership.price}
                                        onChange={(e) => setNewMembership({...newMembership, price: e.target.value})}
                                        placeholder="199"
                                        className="w-full px-4 py-2 border rounded-lg"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Billing Cycle</label>
                                    <select
                                        value={newMembership.billingCycle}
                                        onChange={(e) => setNewMembership({...newMembership, billingCycle: e.target.value})}
                                        className="w-full px-4 py-2 border rounded-lg"
                                    >
                                        <option value="monthly">Monthly</option>
                                        <option value="quarterly">Quarterly</option>
                                        <option value="yearly">Yearly</option>
                                    </select>
                                </div>

                                {/* Benefits */}
                                <div>
                                    <label className="block text-sm font-medium mb-2">Discount % *</label>
                                    <input
                                        type="number"
                                        required
                                        min="0"
                                        max="100"
                                        value={newMembership.discountPercentage}
                                        onChange={(e) => setNewMembership({...newMembership, discountPercentage: e.target.value})}
                                        placeholder="10"
                                        className="w-full px-4 py-2 border rounded-lg"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Max Discount Cap (₹)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={newMembership.maxDiscountPerBooking}
                                        onChange={(e) => setNewMembership({...newMembership, maxDiscountPerBooking: e.target.value})}
                                        placeholder="500"
                                        className="w-full px-4 py-2 border rounded-lg"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Credits per Booking (₹)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={newMembership.creditsPerBooking}
                                        onChange={(e) => setNewMembership({...newMembership, creditsPerBooking: e.target.value})}
                                        placeholder="25"
                                        className="w-full px-4 py-2 border rounded-lg"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Monthly Subsidy (₹)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={newMembership.monthlySubsidy}
                                        onChange={(e) => setNewMembership({...newMembership, monthlySubsidy: e.target.value})}
                                        placeholder="100"
                                        className="w-full px-4 py-2 border rounded-lg"
                                    />
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-sm font-medium mb-2">Subsidy Description</label>
                                    <input
                                        type="text"
                                        value={newMembership.subsidyDescription}
                                        onChange={(e) => setNewMembership({...newMembership, subsidyDescription: e.target.value})}
                                        placeholder="Bookwie-funded small touch-up"
                                        className="w-full px-4 py-2 border rounded-lg"
                                    />
                                </div>

                                {/* Perks */}
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium mb-2">Perks/Features</label>
                                    <div className="flex gap-2 mb-2">
                                        <input
                                            type="text"
                                            value={newPerk.title}
                                            onChange={(e) => setNewPerk({...newPerk, title: e.target.value})}
                                            placeholder="Perk title"
                                            className="flex-1 px-4 py-2 border rounded-lg"
                                        />
                                        <input
                                            type="text"
                                            value={newPerk.description}
                                            onChange={(e) => setNewPerk({...newPerk, description: e.target.value})}
                                            placeholder="Description"
                                            className="flex-1 px-4 py-2 border rounded-lg"
                                        />
                                        <button
                                            type="button"
                                            onClick={addPerk}
                                            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                                        >
                                            <FiPlus />
                                        </button>
                                    </div>
                                    {newMembership.perks.length > 0 && (
                                        <div className="space-y-1">
                                            {newMembership.perks.map((perk, i) => (
                                                <div key={i} className="flex items-center gap-2 text-sm bg-gray-50 p-2 rounded">
                                                    <FiCheck className="text-green-600" />
                                                    <span className="flex-1">{perk.title}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => removePerk(i)}
                                                        className="text-red-500 hover:text-red-700"
                                                    >
                                                        <FiX />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Badge */}
                                <div>
                                    <label className="block text-sm font-medium mb-2">Badge Color</label>
                                    <input
                                        type="color"
                                        value={newMembership.badgeColor}
                                        onChange={(e) => setNewMembership({...newMembership, badgeColor: e.target.value})}
                                        className="w-full h-10 border rounded-lg"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Priority (higher = shown first)</label>
                                    <input
                                        type="number"
                                        value={newMembership.priority}
                                        onChange={(e) => setNewMembership({...newMembership, priority: e.target.value})}
                                        placeholder="0"
                                        className="w-full px-4 py-2 border rounded-lg"
                                    />
                                </div>

                                <div className="col-span-2">
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={newMembership.isPopular}
                                            onChange={(e) => setNewMembership({...newMembership, isPopular: e.target.checked})}
                                            className="w-4 h-4"
                                        />
                                        <span className="text-sm font-medium">Mark as Popular</span>
                                    </label>
                                </div>
                            </div>

                            {/* Submit */}
                            <div className="flex gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => { setShowCreateModal(false); resetForm(); }}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                                >
                                    {editingMembership ? 'Update Membership' : 'Create Membership'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Memberships;

